import { useQuery } from '@apollo/client';
import {
  MagnifyingGlass,
  PauseCircle,
  Pencil,
  PlayCircle,
  Trash,
  UserPlus,
  XCircle,
} from '@phosphor-icons/react';
import { useDebounce } from 'ahooks';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { CopyableField } from 'src/components/CopyableField';
import { Input } from 'src/components/Input';
import { Modal } from 'src/components/Modal';
import {
  SideDropAction,
  SideDropActions,
} from 'src/components/SideDropActions';
import Table, { Header, Row } from 'src/components/Table';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import { GET_WORKFLOW_STATS } from 'src/graphql/queries';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Can, can, Subject } from 'src/hooks/usePermissions';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import {
  PaginatedResponse,
  WorkflowExecutionThreadStatus,
  WorkflowStatus,
  WorkflowWithStatus,
} from 'src/interfaces/workflows.interface';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { ReactElement } from 'react';

interface AutomationsTabProps {
  onNewCampaign?: () => void;
  beta?: boolean;
}

interface WorkflowStatusDto {
  __typename: 'WorkflowStatusesDto';
  workflowReferenceId: string;
  count: number;
  status: string;
}

interface WorkflowRow {
  id: ReactElement;
  referenceId: string;
  version: string | number;
  label: string;
  description: string;
  createdBy: string;
  status: string;
  scheduled: number;
  paused: number;
  completed: number;
  errored: number;
  action: ReactElement;
}

export const AutomationsTab = ({
  onNewCampaign,
  beta = false,
}: AutomationsTabProps) => {
  const { get, post, remove } = useAuthedRequest();
  const notification = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const { role } = useEnvironment();

  const { environment } = useEnvironment();
  const environmentId = environment?.envId;

  const [rows, setRows] = useState<WorkflowRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: string | null;
  }>({ key: null, direction: null });
  const [filter, setFilter] = useState('');
  const debouncedFilter = useDebounce(filter, { wait: 500 });
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deactivateCampaignModalOpen, setDeactivateCampaignModalOpen] =
    useState(false);
  const [activateCampaignModalOpen, setActivateCampaignModalOpen] =
    useState(false);
  const [cancelScheduledCallsModalOpen, setCancelScheduledCallsModalOpen] =
    useState(false);
  const [selectedWorkflow, setSelectedWorkflow] =
    useState<WorkflowWithStatus | null>(null);
  const [workflowReferenceIds, setWorkflowReferenceIds] = useState<string[]>(
    [],
  );

  const hasShownErrorRef = useRef(false);

  const {
    data: workflowStatsData,
    loading: workflowStatsLoading,
    error: workflowStatsError,
  } = useQuery(GET_WORKFLOW_STATS, {
    variables: {
      environmentId: environmentId,
      workflowReferenceIds: workflowReferenceIds,
    },
    skip: workflowReferenceIds.length === 0,
  });

  const headers: Header[] = [
    { key: 'id', label: 'ID', width: '8rem', disableSorting: true },
    { key: 'label', label: 'Campaign Name', width: '20%' },
    { key: 'description', label: 'Description', width: '30%' },
    { key: 'status', label: 'Status', width: '8%' },
    {
      key: 'scheduled',
      label: '# Scheduled',
      width: '6rem',
      disableSorting: true,
    },
    {
      key: 'paused',
      label: '# Inactive',
      width: '6rem',
      disableSorting: true,
    },
    {
      key: 'completed',
      label: '# Completed',
      width: '6rem',
      disableSorting: true,
    },
    { key: 'errored', label: '# Errored', width: '6rem', disableSorting: true },
    {
      key: 'action',
      label: '',
      width: '2%',
      disableSorting: true,
      align: 'center',
    },
  ];

  const handleAddContacts = useCallback(
    (workflow: WorkflowWithStatus) => {
      navigate(`/campaigns/${workflow.referenceId}/contacts`);
    },
    [navigate],
  );

  const fetchWorkflows = useCallback(
    async (page: number, pageSize: number) => {
      setLoading(true);
      try {
        const queryObject: QueryObject = {
          page: String(page),
          size: String(pageSize),
          filters: JSON.stringify({
            label: debouncedFilter,
          }),
        };

        if (sortConfig.key) {
          queryObject.sort_column = sortConfig.key;
          queryObject.sort_desc =
            sortConfig.direction === 'desc' ? 'true' : 'false';
        }

        const query = new URLSearchParams(
          queryObject as Record<string, string>,
        );

        const data: PaginatedResponse<WorkflowWithStatus> = await get(
          `/workflows?${query.toString()}`,
        );
        // Map each workflow into a row for the table.

        const workflowsIds: string[] = [];
        const items = data.items.map((workflow: WorkflowWithStatus) => {
          workflowsIds.push(workflow.referenceId);

          const { schedule } = workflow.config;

          // Add null check for startDate
          let campaignStartDate = '';
          let status = '';

          if (schedule.startDate) {
            const zonedStartDate = toZonedTime(
              schedule.startDate,
              schedule.timeZone,
            );
            campaignStartDate = format(zonedStartDate, 'yyyy-MM-dd');
          }

          switch (workflow.status) {
            case WorkflowStatus.WORKFLOW_ACTIVE:
              // If the campaign is scheduled to start in the future, show the start date.
              if (schedule.startDate && campaignStartDate) {
                const zonedStartDate = toZonedTime(
                  schedule.startDate,
                  schedule.timeZone,
                );
                if (zonedStartDate > new Date()) {
                  status = `Scheduled to start on ${campaignStartDate}`;
                } else {
                  status = 'Active';
                }
              } else {
                status = 'Active';
              }
              break;
            case WorkflowStatus.WORKFLOW_PAUSED:
              status = 'Inactive';
              break;
          }

          const actions: SideDropAction[] = [
            {
              label: can(role, 'update', Subject.CAMPAIGNS) ? 'Edit' : 'View',
              Icon: Pencil,
              onClick: () =>
                navigate(
                  beta
                    ? `/beta/campaigns/overview/${workflow.referenceId}`
                    : `/campaigns/edit/${workflow.referenceId}`,
                ),
            },
            {
              label: 'Add Contacts',
              Icon: UserPlus,
              onClick: () => handleAddContacts(workflow),
              show: can(role, 'update', Subject.CAMPAIGNS),
            },
          ];
          if (workflow.status === WorkflowStatus.WORKFLOW_PAUSED) {
            actions.push({
              label: 'Activate Campaign',
              Icon: PlayCircle,
              onClick: () => openActivateCampaignModal(workflow),
            });
          } else {
            actions.push({
              label: 'Deactivate Campaign',
              Icon: PauseCircle,
              onClick: () => openDeactivateCampaignModal(workflow),
            });
          }
          actions.push({
            label: 'Cancel Scheduled Calls',
            Icon: XCircle,
            onClick: () => openCancelScheduledCallsModal(workflow),
            show: can(role, 'update', Subject.CAMPAIGNS),
          });
          actions.push({
            label: 'Delete',
            Icon: Trash,
            color: 'error',
            onClick: () => openDeleteWorfklowModal(workflow),
            show: can(role, 'delete', Subject.CAMPAIGNS),
          });

          return {
            id: (
              <CopyableField value={workflow.id} notification={notification} />
            ),
            referenceId: workflow.referenceId,
            version: workflow.version,
            label: workflow.label,
            description: workflow.description,
            createdBy: workflow.environmentId ? 'User' : 'Fluents',
            status,
            scheduled: 0,
            paused: 0,
            completed: 0,
            errored: 0,
            action: (
              <div
                className="flex justify-center items-center"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <SideDropActions options={actions} />
              </div>
            ),
          };
        });
        setWorkflowReferenceIds(workflowsIds);
        setRows(items);
        setTotalItems(data.total);
      } catch (error) {
        console.error('Error fetching workflows:', error);
      } finally {
        setLoading(false);
      }
    },
    [
      debouncedFilter,
      sortConfig.key,
      sortConfig.direction,
      get,
      role,
      notification,
      navigate,
      handleAddContacts,
    ],
  );

  useEffect(() => {
    fetchWorkflows(currentPage, currentPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, currentPageSize, sortConfig, debouncedFilter]);

  const handlePageChange = (page: number, perPage: number) => {
    setCurrentPage(page);
    setCurrentPageSize(perPage);
  };

  const handleSizeChange = (perPage: number) => {
    setCurrentPageSize(perPage);
    setCurrentPage(1);
  };

  const handleSort = (key: string, direction: string) => {
    setSortConfig({ key, direction });
  };

  const openCancelScheduledCallsModal = (workflow: WorkflowWithStatus) => {
    setSelectedWorkflow(workflow);
    setCancelScheduledCallsModalOpen(true);
  };

  const openDeleteWorfklowModal = (workflow: WorkflowWithStatus) => {
    setSelectedWorkflow(workflow);
    setDeleteModalOpen(true);
  };

  const openDeactivateCampaignModal = (workflow: WorkflowWithStatus) => {
    setSelectedWorkflow(workflow);
    setDeactivateCampaignModalOpen(true);
  };

  const openActivateCampaignModal = (workflow: WorkflowWithStatus) => {
    setSelectedWorkflow(workflow);
    setActivateCampaignModalOpen(true);
  };

  const handleRemoveWorkflow = async () => {
    try {
      await remove(`/workflows/${selectedWorkflow?.referenceId}`);
      notification.success('Successfully deleted campaign');
      setDeleteModalOpen(false);
      setCurrentPage(1);
      fetchWorkflows(1, currentPageSize);
    } catch {
      notification.error('Failed to delete campaign');
    }
  };

  const handleCancelScheduledCalls = async () => {
    if (!selectedWorkflow) return;
    try {
      await post(`/workflows/${selectedWorkflow.referenceId}/cancel`, {});
      notification.success('Successfully cancelled scheduled calls');
      setCancelScheduledCallsModalOpen(false);
    } catch (error) {
      notification.error('Failed to cancel scheduled calls');
      console.error(error);
    }
  };

  const handleDeactivateCampaign = async () => {
    if (!selectedWorkflow) return;
    try {
      await post(`/workflows/${selectedWorkflow.referenceId}/pause`, {});
      notification.success('Successfully deactivated campaign');
      setDeactivateCampaignModalOpen(false);
      fetchWorkflows(currentPage, currentPageSize);
    } catch (error) {
      notification.error('Failed to deactivate campaign');
      console.error(error);
    }
  };

  const handleActivateCampaign = async () => {
    if (!selectedWorkflow) return;
    try {
      await post(`/workflows/${selectedWorkflow.referenceId}/unpause`, {});
      notification.success('Successfully activated campaign');
      setActivateCampaignModalOpen(false);
      fetchWorkflows(currentPage, currentPageSize);
    } catch (error) {
      notification.error('Failed to activate campaign');
      console.error(error);
    }
  };

  useEffect(() => {
    if (workflowStatsLoading || !workflowStatsData) return;

    const statusMap = new Map<string, Map<string, number>>();

    workflowStatsData.getWorkflowStats.forEach((stat: WorkflowStatusDto) => {
      const { workflowReferenceId, status, count } = stat;

      if (!statusMap.has(workflowReferenceId)) {
        statusMap.set(workflowReferenceId, new Map<string, number>());
      }

      const workflowStatusMap = statusMap.get(workflowReferenceId)!;
      workflowStatusMap.set(status, count);
    });

    setRows((currentRows) => {
      return currentRows.map((row) => {
        let workflowStatusMap = statusMap.get(row.referenceId as string);
        if (!workflowStatusMap) {
          workflowStatusMap = new Map<string, number>();
        }

        const scheduled =
          (workflowStatusMap.get(WorkflowExecutionThreadStatus.RUNNING) || 0) +
          (workflowStatusMap.get(WorkflowExecutionThreadStatus.RATE_LIMITED) ||
            0);
        const paused =
          workflowStatusMap.get(WorkflowExecutionThreadStatus.PAUSED) || 0;
        const completed =
          workflowStatusMap.get(WorkflowExecutionThreadStatus.COMPLETED) || 0;
        const errored =
          workflowStatusMap.get(WorkflowExecutionThreadStatus.ERRORED) || 0;

        return {
          ...row,
          scheduled,
          paused,
          completed,
          errored,
        };
      });
    });
  }, [workflowStatsData, workflowStatsLoading, workflowReferenceIds]);

  const renderedRows: Row[] = rows.map((row) => {
    return {
      ...row,
      scheduled: workflowStatsLoading ? (
        <Skeleton />
      ) : row.scheduled > 0 ? (
        row.scheduled.toLocaleString()
      ) : (
        '—'
      ),
      paused: workflowStatsLoading ? (
        <Skeleton />
      ) : row.paused > 0 ? (
        row.paused.toLocaleString()
      ) : (
        '—'
      ),
      completed: workflowStatsLoading ? (
        <Skeleton />
      ) : row.completed > 0 ? (
        row.completed.toLocaleString()
      ) : (
        '—'
      ),
      errored: workflowStatsLoading ? (
        <Skeleton />
      ) : row.errored > 0 ? (
        row.errored.toLocaleString()
      ) : (
        '—'
      ),
    };
  });

  useEffect(() => {
    if (
      workflowStatsError &&
      workflowReferenceIds.length > 0 &&
      !hasShownErrorRef.current
    ) {
      if (process.env.REACT_APP_GRAPHQL_IGNORE_ERRORS) {
        console.warn('GraphQL error ignored:', workflowStatsError);
      } else {
        notification.error('Failed to load campaign statistics.');
      }
      hasShownErrorRef.current = true;
    }
  }, [workflowStatsError, workflowReferenceIds.length]);

  useEffect(() => {
    hasShownErrorRef.current = false;
  }, [workflowReferenceIds]);

  useEffect(() => {
    const errorMessage = location.state?.error;
    if (errorMessage) {
      const errorKey = `campaign-not-found-${location.pathname}`;
      const hasShownError = sessionStorage.getItem(errorKey);

      if (!hasShownError) {
        sessionStorage.setItem(errorKey, 'true');
        notification.error(errorMessage);
      }
    }
  }, [location.state?.error, notification, location.pathname]);

  return (
    <ContentWrapper>
      <div className="flex flex-col md:flex-row justify-between items-center mb-6">
        <div className="flex w-full md:w-96">
          <Can operation="read" subject={Subject.CAMPAIGNS}>
            <Input
              type="text"
              required
              placeholder="Search campaigns"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  fetchWorkflows(currentPage, currentPageSize);
                }
              }}
              icon={<MagnifyingGlass size={20} />}
              iconPosition="left"
              inputAction={() => fetchWorkflows(currentPage, currentPageSize)}
            />
          </Can>
        </div>
        <div className="flex mt-4 md:mt-0">
          <Can operation="create" subject={Subject.CAMPAIGNS}>
            <Button
              onClick={() =>
                onNewCampaign ? onNewCampaign() : navigate('/campaigns/create')
              }
              className="h-11"
            >
              New Campaign
            </Button>
          </Can>
        </div>
      </div>

      <Can operation="read" subject={Subject.CAMPAIGNS}>
        <div className="text-right text-neutral-500 italic text-sm mb-4">
          Campaign statistics may be delayed by up to 5 minutes.
        </div>
        <Table
          headers={headers}
          rows={renderedRows}
          totalItems={totalItems}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onSort={handleSort}
          loading={loading}
          setSize={handleSizeChange}
          defaultSize={currentPageSize}
          onRowClick={(index: number) => {
            const workflow = rows[index] as unknown as WorkflowWithStatus;
            if (workflow) {
              navigate(
                beta
                  ? `/beta/campaigns/overview/${workflow.referenceId}`
                  : `/campaigns/edit/${workflow.referenceId}`,
              );
            }
          }}
        />
      </Can>
      {/* Delete Campaign Modal */}
      <Modal
        title={`Delete Campaign: ${selectedWorkflow?.label || ''}`}
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedWorkflow(null);
        }}
        actionButton={
          <Button onClick={handleRemoveWorkflow}>Delete Campaign</Button>
        }
        className="w-auto max-w-md"
      >
        <div className="text-center">
          Are you sure you want to delete this campaign?
        </div>
      </Modal>
      {/* Cancel Scheduled Calls Modal */}
      <Modal
        title={`Cancel Scheduled Calls: ${selectedWorkflow?.label || ''}`}
        isOpen={cancelScheduledCallsModalOpen}
        onClose={() => {
          setCancelScheduledCallsModalOpen(false);
          setSelectedWorkflow(null);
        }}
        actionButton={
          <Button onClick={handleCancelScheduledCalls}>
            Cancel Scheduled Calls
          </Button>
        }
        className="w-auto max-w-md"
      >
        <div className="text-center">
          Are you sure you want to cancel all scheduled calls for this campaign?
        </div>
      </Modal>
      {/* Deactivate Campaign Modal */}
      <Modal
        title={`Deactivate Campaign: ${selectedWorkflow?.label || ''}`}
        isOpen={deactivateCampaignModalOpen}
        onClose={() => {
          setDeactivateCampaignModalOpen(false);
          setSelectedWorkflow(null);
        }}
        actionButton={
          <Button onClick={handleDeactivateCampaign}>
            Deactivate Campaign
          </Button>
        }
        className="w-auto max-w-md"
      >
        <div className="text-center">
          Are you sure you want to deactivate this campaign?
        </div>
      </Modal>
      {/* Activate Campaign Modal */}
      <Modal
        title={`Activate Campaign: ${selectedWorkflow?.label || ''}`}
        isOpen={activateCampaignModalOpen}
        onClose={() => {
          setActivateCampaignModalOpen(false);
          setSelectedWorkflow(null);
        }}
        actionButton={
          <Button onClick={handleActivateCampaign}>Activate Campaign</Button>
        }
        className="w-auto max-w-md"
      >
        <div className="text-center">
          Are you sure you want to activate this campaign?
        </div>
      </Modal>
    </ContentWrapper>
  );
};
